// standards.js — bodyweight- and sex-relative strength levels for the main lifts.
//
// Source: Strength Level (strengthlevel.com/strength-standards), the widely used
// public standards built from millions of user-logged lifts. Ratios are a lift's
// one-rep max divided by body weight, for adult lifters (their tables hold flat
// from about 18 to 40 and taper after 45). Pull-ups use their rep standards.
//   https://strengthlevel.com/strength-standards/squat
//   https://strengthlevel.com/strength-standards/bench-press
//   https://strengthlevel.com/strength-standards/deadlift
//   https://strengthlevel.com/strength-standards/shoulder-press
//   https://strengthlevel.com/strength-standards/bent-over-row
//   https://strengthlevel.com/strength-standards/pull-ups
// Level meaning (Strength Level): Beginner = stronger than 5% of lifters,
// Novice 20%, Intermediate 50%, Advanced 80%, Elite 95%.
//
// Rules: no body weight → no level (the UI asks for one). No sex → no level
// (the UI asks once and stores state.profile.sex).

import { completedSessions, e1rm, isWorkingSet, indexExercises } from './analytics.js';

const DAY = 86400000;
export const LEVELS = ['Beginner', 'Novice', 'Intermediate', 'Advanced', 'Elite'];
export const LEVEL_BLURB = {
  Beginner: 'Stronger than about 5% of lifters',
  Novice: 'Stronger than about 20% of lifters',
  Intermediate: 'Stronger than about 50% of lifters',
  Advanced: 'Stronger than about 80% of lifters',
  Elite: 'Stronger than about 95% of lifters',
};

// 1RM ÷ body weight thresholds [Beginner, Novice, Intermediate, Advanced, Elite].
// Pull-ups: bodyweight reps (Strength Level lists "< 1" for the lowest tiers → 0).
export const STANDARDS = {
  squat: { male: [0.75, 1.25, 1.75, 2.25, 2.75], female: [0.5, 0.75, 1.25, 1.75, 2.25] },
  bench: { male: [0.5, 1.0, 1.25, 1.5, 2.0], female: [0.3, 0.5, 0.75, 1.1, 1.45] },
  deadlift: { male: [1.0, 1.5, 2.0, 2.5, 3.25], female: [0.75, 1.0, 1.5, 2.0, 2.5] },
  ohp: { male: [0.35, 0.55, 0.8, 1.05, 1.35], female: [0.2, 0.35, 0.5, 0.7, 0.95] },
  row: { male: [0.5, 0.75, 1.0, 1.5, 1.75], female: [0.3, 0.45, 0.7, 0.95, 1.25] },
  pullup: { male: [0, 7, 13, 21, 30], female: [0, 0, 6, 12, 19], reps: true },
};

export const LIFTS = [
  { key: 'squat', label: 'Squat', ids: ['x_squat', 'fx_Barbell_Squat'],
    test: (n, eq) => eq === 'Barbell' && /squat/.test(n) && !/front|split|hack|box|overhead|zercher|goblet|jump|smith|bulgarian|lunge|sissy|pistol|side|bench/.test(n) },
  { key: 'bench', label: 'Bench press', ids: ['x_bench', 'fx_Barbell_Bench_Press_-_Medium_Grip'],
    test: (n, eq) => eq === 'Barbell' && /bench press/.test(n) && !/incline|decline|close|reverse|floor|board|dumbbell|smith|bands|chains|one arm/.test(n) },
  { key: 'deadlift', label: 'Deadlift', ids: ['x_dead', 'fx_Barbell_Deadlift', 'fx_Sumo_Deadlift'],
    test: (n, eq) => eq === 'Barbell' && /deadlift/.test(n) && !/romanian|stiff|rdl|deficit|rack|single|snatch|trap|bands|chains|reverse/.test(n) },
  { key: 'ohp', label: 'Overhead press', ids: ['x_ohp', 'fx_Standing_Military_Press'],
    test: (n, eq) => eq === 'Barbell' && /(overhead press|military press|shoulder press)/.test(n) && !/behind|seated|push press|smith|bradford/.test(n) },
  { key: 'row', label: 'Barbell row', ids: ['fx_Bent_Over_Barbell_Row'],
    test: (n, eq) => eq === 'Barbell' && /(bent.?over.*row|barbell row|pendlay)/.test(n) && !/dumbbell|one.?arm|t-bar|rear delt|upright|inverted/.test(n) },
  { key: 'pullup', label: 'Pull-up', ids: ['x_pullup', 'fx_Pullups', 'fx_Chin-Up', 'fx_V-Bar_Pullup'],
    test: (n) => /(pull-?ups?\b|chin-?ups?\b)/.test(n) && !/assisted|band|pulldown|machine|kipping|one arm|rocky/.test(n) },
];
const LIFT_BY_KEY = Object.fromEntries(LIFTS.map(l => [l.key, l]));

// Which standard lift (if any) an exercise counts toward.
export function liftKeyFor(ex) {
  if (!ex) return null;
  for (const l of LIFTS) if (l.ids.includes(ex.id)) return l.key;
  const n = String(ex.name || '').toLowerCase();
  for (const l of LIFTS) if (l.test(n, ex.equipment)) return l.key;
  return null;
}

// Latest body weight in lb: the newest weigh-in, else profile.bodyweight (lb).
export function bodyweightLb(state) {
  const es = (state.bodyEntries || []).filter(e => e && e.date && e.weight > 0).sort((a, b) => (a.date < b.date ? -1 : 1));
  if (es.length) return { lb: es[es.length - 1].weight, date: es[es.length - 1].date, source: 'weigh-in' };
  const p = state.profile || {};
  if (p.bodyweight > 0) return { lb: +p.bodyweight, date: null, source: 'profile' };
  return null;
}
export function sexOf(state) {
  const s = state.profile && state.profile.sex;
  return s === 'male' || s === 'female' ? s : null;
}

// Best estimated strength on a lift: e1RM (lb) for barbell lifts; for pull-ups,
// bodyweight reps (weighted sets converted to equivalent reps via Epley).
// Prefers the last 12 weeks; falls back to the all-time best (flagged).
export function liftBest(state, key, bwLb, now = Date.now()) {
  const idx = indexExercises(state);
  const reps = !!STANDARDS[key]?.reps;
  let recent = null, ever = null;
  for (const s of completedSessions(state)) {
    for (const st of s.sets || []) {
      if (!isWorkingSet(st)) continue;
      const ex = idx[st.exerciseId] || { id: st.exerciseId };
      if (liftKeyFor(ex) !== key) continue;
      let v;
      if (reps) {
        if (st.w > 0) {
          if (!(bwLb > 0)) continue;
          v = Math.max(st.reps, 30 * (e1rm(bwLb + st.w, st.reps) / bwLb - 1));
        } else v = st.reps;
        v = Math.round(v);
      } else {
        if (!(st.w > 0) || st.reps > 12) continue;
        v = e1rm(st.w, st.reps);
      }
      const rec = { value: v, exerciseId: st.exerciseId, date: s.startedAt, w: st.w, reps: st.reps };
      if (!ever || v > ever.value) ever = rec;
      if (s.startedAt >= now - 84 * DAY && (!recent || v > recent.value)) recent = rec;
    }
  }
  if (recent) return { ...recent, recent: true };
  if (ever) return { ...ever, recent: false };
  return null;
}

// Level for a value (ratio, or reps for pull-ups).
//   → { index (-1 below Beginner … 4 Elite), label, next, progress 0..1, nextAt (ratio | reps) }
export function levelFor(key, value, sex) {
  const t = STANDARDS[key] && STANDARDS[key][sex];
  if (!t || !(value >= 0)) return null;
  let i = -1;
  for (let k = 0; k < t.length; k++) if (value >= t[k]) i = k;
  const label = i < 0 ? 'Getting started' : LEVELS[i];
  if (i >= t.length - 1) return { index: i, label, next: null, progress: 1, nextAt: null, thresholds: t };
  const lo = i < 0 ? 0 : t[i];
  const hi = t[i + 1];
  return { index: i, label, next: LEVELS[i + 1], progress: Math.max(0, Math.min(1, (value - lo) / (hi - lo || 1))), nextAt: hi, thresholds: t };
}

// Everything the UI needs for one lift.
export function liftStandard(state, key, now = Date.now()) {
  const lift = LIFT_BY_KEY[key];
  if (!lift) return null;
  const bw = bodyweightLb(state);
  const sex = sexOf(state);
  const best = liftBest(state, key, bw && bw.lb, now);
  const reps = !!STANDARDS[key].reps;
  const out = { key, label: lift.label, reps, best, bodyweight: bw, sex, need: null, level: null, ratio: null, nextLoadLb: null };
  if (!bw) { out.need = 'bodyweight'; return out; }
  if (!sex) { out.need = 'sex'; return out; }
  if (!best) { out.need = 'history'; return out; }
  const value = reps ? best.value : best.value / bw.lb;
  out.ratio = reps ? null : value;
  out.level = levelFor(key, value, sex);
  if (!reps && out.level && out.level.nextAt) out.nextLoadLb = out.level.nextAt * bw.lb;
  return out;
}

export function strengthStandards(state, now = Date.now()) {
  const bw = bodyweightLb(state);
  const sex = sexOf(state);
  return {
    bodyweight: bw, sex,
    need: !bw ? 'bodyweight' : !sex ? 'sex' : null,
    lifts: LIFTS.map(l => liftStandard(state, l.key, now)),
  };
}
